import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Col, Input, Label } from 'reactstrap';
import SettingsSelect from 'pages/SettingsV2/SettingsSelect';
import {
  SALE_CODE_DIGIT_OPTIONS,
  SALE_CODE_FORMAT_OPTIONS,
  SALE_CODE_TYPE_OPTIONS,
  SALE_CODE_YEAR_OPTIONS,
} from 'pages/SettingsV2/SettingsConstants';
import {
  SaleCodeFormat,
  SaleCodeType,
  SaleCodeYearType,
  SettingsFormValues,
} from 'pages/SettingsV2/settingsTypes';
import { useSettings } from 'pages/SettingsV2/SettingsContext';
import SettingsV2SectionCard from 'pages/SettingsV2/SettingsV2SectionCard';

const InvoiceCodeSectionV2 = () => {
  const { t } = useTranslation();
  const { fieldErrors, clearFieldError, validateField } = useSettings();
  const { values, setFieldValue, handleChange, handleBlur } =
    useFormikContext<SettingsFormValues>();
  const { prefix, type, typeYear, format, num } = values.invoiceCode;
  const dateMode = type === 2;

  return (
    <SettingsV2SectionCard
      sectionKey="invoiceCode"
      title={t('Invoice Code Settings')}
      icon="ri-hashtag"
    >
      <div className="mb-3 row">
        <Label htmlFor="invoice-v2-prefix" className="col-sm-3 col-form-label">
          {t('Code Prefix')}
        </Label>
        <Col sm={9}>
          <Input
            id="invoice-v2-prefix"
            type="text"
            name="invoiceCode.prefix"
            value={prefix}
            maxLength={5}
            onChange={(e) => {
              handleChange(e);
              clearFieldError('invoiceCode.prefix');
            }}
            onBlur={(e) => {
              handleBlur(e);
              validateField('invoiceCode.prefix');
            }}
            placeholder={t('e.g. INV')}
          />
        </Col>
      </div>

      <div className="mb-3 row">
        <Label htmlFor="invoice-v2-type" className="col-sm-3 col-form-label">
          {t('Numbering Type')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="invoice-v2-type"
            value={type}
            onChange={(value) => {
              const next = Number(value) as SaleCodeType;
              setFieldValue('invoiceCode.type', next);
              clearFieldError('invoiceCode.typeYear');
              clearFieldError('invoiceCode.format');
              clearFieldError('invoiceCode.num');

              if (next !== 2) {
                setFieldValue('invoiceCode.typeYear', 0);
                setFieldValue('invoiceCode.format', '');
                setFieldValue('invoiceCode.num', 0);
              } else {
                if (typeYear === 0) setFieldValue('invoiceCode.typeYear', 1);
                if (!format) setFieldValue('invoiceCode.format', 'ym');
                if (num < 2) setFieldValue('invoiceCode.num', 4);
              }
            }}
            options={SALE_CODE_TYPE_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onBlur={() => validateField('invoiceCode.type')}
          />
        </Col>
      </div>

      <div className="mb-3 row">
        <Label
          htmlFor="invoice-v2-year-type"
          className="col-sm-3 col-form-label"
        >
          {t('Year Type')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="invoice-v2-year-type"
            value={typeYear}
            isDisabled={!dateMode}
            onChange={(value) => {
              setFieldValue(
                'invoiceCode.typeYear',
                Number(value) as SaleCodeYearType
              );
              clearFieldError('invoiceCode.typeYear');
            }}
            options={SALE_CODE_YEAR_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onBlur={() => validateField('invoiceCode.typeYear')}
          />
          {fieldErrors['invoiceCode.typeYear'] ? (
            <div className="text-danger mt-1 err-txt">
              {t(fieldErrors['invoiceCode.typeYear'] as string)}
            </div>
          ) : null}
        </Col>
      </div>

      <div className="mb-3 row">
        <Label htmlFor="invoice-v2-format" className="col-sm-3 col-form-label">
          {t('Date Format')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="invoice-v2-format"
            value={format}
            isDisabled={!dateMode}
            onChange={(value) => {
              setFieldValue('invoiceCode.format', value as SaleCodeFormat);
              clearFieldError('invoiceCode.format');
            }}
            options={SALE_CODE_FORMAT_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            onBlur={() => validateField('invoiceCode.format')}
          />
          {fieldErrors['invoiceCode.format'] ? (
            <div className="text-danger mt-1 err-txt">
              {t(fieldErrors['invoiceCode.format'] as string)}
            </div>
          ) : null}
        </Col>
      </div>

      <div className="row">
        <Label htmlFor="invoice-v2-num" className="col-sm-3 col-form-label">
          {t('Number of Digits')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="invoice-v2-num"
            value={num}
            isDisabled={!dateMode}
            onChange={(value) => {
              setFieldValue('invoiceCode.num', Number(value));
              clearFieldError('invoiceCode.num');
            }}
            options={SALE_CODE_DIGIT_OPTIONS.map((digit) => ({
              value: digit,
              label: String(digit),
            }))}
            onBlur={() => validateField('invoiceCode.num')}
          />
          {fieldErrors['invoiceCode.num'] ? (
            <div className="text-danger mt-1 err-txt">
              {t(fieldErrors['invoiceCode.num'] as string)}
            </div>
          ) : null}
        </Col>
      </div>
    </SettingsV2SectionCard>
  );
};

export default InvoiceCodeSectionV2;
